import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Debt, DebtPlan, Organization } from '@nargus/shared';
import { Link } from '@tanstack/react-router';
import { Check, ChevronRight, LoaderCircle, Plus } from 'lucide-react';
import { useCreateDebt, useDebts, useUpdateDebt } from '@/resources/debt/debt.service';
import { useSession } from '@/resources/auth/auth.service';
import { useStaffMembers } from '@/resources/organization/organization.service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PaymentDialog } from './debt-payment-dialog';
import {
  ErrorNote,
  SectionHeader,
  daysUntil,
  formatDate,
  formatEuro,
  formatRelativeDays,
  fromDateInput,
  toAmountInput,
  toCents,
  toDateInput,
} from './ui';

type Bucket = 'overdue' | 'soon' | 'later' | 'undated';

const EVERYONE = 'everyone';

const BUCKET_ORDER: Bucket[] = ['overdue', 'soon', 'later', 'undated'];
const SOON_DAYS = 7;
const RELATIVE_DAYS = 14;

const SPINE: Record<Bucket, string> = {
  overdue: 'bg-destructive',
  soon: 'bg-warn',
  later: 'bg-border',
  undated: 'bg-border',
};

const DUE_TONE: Record<Bucket, string> = {
  overdue: 'text-destructive',
  soon: 'text-warn',
  later: 'text-muted-foreground',
  undated: 'text-muted-foreground',
};

const EYEBROW = 'text-xs uppercase tracking-[0.14em]';

function bucketOf(debt: Debt): Bucket {
  if (!debt.nextDueAt) return 'undated';
  const days = daysUntil(debt.nextDueAt);
  if (days < 0) return 'overdue';
  return days <= SOON_DAYS ? 'soon' : 'later';
}

function percentPaid(debt: Pick<Debt, 'paidCents' | 'totalCents'>): number {
  if (debt.totalCents <= 0) return 0;
  return Math.min(100, Math.round((debt.paidCents / debt.totalCents) * 100));
}

export function DebtsSection({ organization }: { organization: Organization }) {
  const { t } = useTranslation();
  const isAdmin = organization.role === 'owner';
  const [person, setPerson] = useState(EVERYONE);
  const debts = useDebtList(organization.id, isAdmin ? person : EVERYONE);
  const showPerson = isAdmin && person === EVERYONE;

  return (
    <div className="rise flex max-w-2xl flex-col gap-8">
      <div className="flex items-start justify-between gap-4">
        <SectionHeader title={t('debts.title')} hint={t('debts.hint')} />
        <Button
          size="icon"
          className="shrink-0"
          aria-label={t('debts.addShort')}
          render={
            <Link
              to="/organizations/$organizationId/debts/new"
              params={{ organizationId: organization.id }}
            />
          }
        >
          <Plus />
        </Button>
      </div>

      {isAdmin && (
        <PersonFilter organizationId={organization.id} value={person} onChange={setPerson} />
      )}

      {debts.isPending ? (
        <ListSkeleton />
      ) : debts.all.length > 0 ? (
        <>
          <Totals debts={debts.all} />
          <Schedule
            debts={debts.open}
            organization={organization}
            showPerson={showPerson}
          />
          {debts.settled.length > 0 && (
            <Settled
              debts={debts.settled}
              organization={organization}
              showPerson={showPerson}
            />
          )}
        </>
      ) : (
        !debts.error && <Empty organization={organization} />
      )}

      <ErrorNote error={debts.error} />
    </div>
  );
}

function useDebtList(organizationId: string, person: string) {
  const query = useDebts(organizationId);
  const all = (query.data ?? []).filter(
    (debt) => person === EVERYONE || debt.debtorId === person,
  );

  return {
    all,
    open: all.filter((debt) => !debt.settledAt),
    settled: all.filter((debt) => debt.settledAt),
    isPending: query.isPending,
    error: query.error,
  };
}

function PersonFilter({
  organizationId,
  value,
  onChange,
}: {
  organizationId: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  const members = useStaffMembers(organizationId);

  const labels: Record<string, string> = {
    [EVERYONE]: t('debts.personAll'),
    ...Object.fromEntries(
      (members.data ?? []).map((member) => [member.userId, member.name ?? member.email]),
    ),
  };

  return (
    <Select
      items={labels}
      value={value}
      onValueChange={(next) => onChange(String(next ?? EVERYONE))}
    >
      <SelectTrigger aria-label={t('debts.personFilterLabel')} className="w-full sm:w-64">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={EVERYONE}>{t('debts.personAll')}</SelectItem>
        {members.data?.map((member) => (
          <SelectItem key={member.userId} value={member.userId}>
            {member.name ?? member.email}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function useBucketLabels(): Record<Bucket, string> {
  const { t } = useTranslation();
  return {
    overdue: t('debts.groupOverdue'),
    soon: t('debts.groupThisWeek'),
    later: t('debts.groupLater'),
    undated: t('debts.groupUndated'),
  };
}

function Totals({ debts }: { debts: Debt[] }) {
  const { t } = useTranslation();
  const total = debts.reduce((sum, debt) => sum + debt.totalCents, 0);
  const paid = debts.reduce((sum, debt) => sum + debt.paidCents, 0);
  const remaining = debts.reduce((sum, debt) => sum + debt.remainingCents, 0);
  const percent = percentPaid({ paidCents: paid, totalCents: total });

  const open = debts.filter((debt) => !debt.settledAt);
  const monthly = open
    .filter((debt) => debt.plan === 'installment')
    .reduce((sum, debt) => sum + (debt.installmentCents ?? 0), 0);
  const dueThisMonth = open
    .filter((debt) => debt.plan === 'installment' && !debt.currentPeriodPaid)
    .reduce((sum, debt) => sum + (debt.nextDueCents ?? 0), 0);

  return (
    <div className="flex flex-col gap-4 border-b pb-6">
      <div className="flex flex-wrap items-end justify-between gap-x-10 gap-y-4">
        <div>
          <p className={`${EYEBROW} text-muted-foreground`}>{t('debts.openTotalLabel')}</p>
          <p className="font-heading text-3xl font-medium tabular-nums">
            {formatEuro(remaining)}
          </p>
        </div>

        {monthly > 0 && (
          <div className="text-right">
            <p className={`${EYEBROW} text-muted-foreground`}>{t('debts.summaryMonthly')}</p>
            <p className="text-lg font-medium tabular-nums">{formatEuro(monthly)}</p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <ProgressRule percent={percent} total={total} className="h-1" />
        <p className="text-xs text-muted-foreground tabular-nums">
          {t('debts.summaryProgress', { percent })} ·{' '}
          {t('debts.paidOf', { paid: formatEuro(paid), total: formatEuro(total) })}
        </p>
      </div>

      {dueThisMonth > 0 && (
        <p className="text-sm text-warn tabular-nums">
          {t('debts.summaryDueThisMonth', { amount: formatEuro(dueThisMonth) })}
        </p>
      )}
    </div>
  );
}

function ProgressRule({
  percent,
  total,
  className,
}: {
  percent: number;
  total: number;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={t('debts.progressAria', { percent, total: formatEuro(total) })}
      className={`overflow-hidden rounded-full bg-muted ${className ?? ''}`}
    >
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-500"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function GroupHeading({ label, count, tone }: { label: string; count: number; tone?: string }) {
  return (
    <div className="flex items-center gap-3">
      <h2 className={`${EYEBROW} ${tone ?? 'text-muted-foreground'}`}>{label}</h2>
      <span className="h-px flex-1 bg-border" />
      <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
    </div>
  );
}

function personOf(debt: Debt): string | undefined {
  return debt.debtor?.name ?? debt.debtor?.email;
}

function Schedule({
  debts,
  organization,
  showPerson,
}: {
  debts: Debt[];
  organization: Organization;
  showPerson: boolean;
}) {
  const { t } = useTranslation();
  const labels = useBucketLabels();

  if (debts.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('debts.allSettled')}</p>;
  }

  const groups = BUCKET_ORDER.map((bucket) => ({
    bucket,
    debts: debts.filter((debt) => bucketOf(debt) === bucket),
  })).filter((group) => group.debts.length > 0);

  return (
    <div className="flex flex-col gap-7">
      {groups.map((group) => (
        <section key={group.bucket} className="flex flex-col gap-1">
          <GroupHeading
            label={labels[group.bucket]}
            count={group.debts.length}
            tone={group.bucket === 'overdue' ? 'text-destructive' : undefined}
          />
          {group.debts.map((debt) => (
            <Row
              key={debt.id}
              debt={debt}
              organization={organization}
              showPerson={showPerson}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function useMetaLine(debt: Debt, person?: string): string {
  const { t } = useTranslation();

  const plan =
    debt.plan === 'installment' && debt.installmentCents && debt.dueDay
      ? t('debts.planInstallmentSummary', {
          amount: formatEuro(debt.installmentCents),
          day: debt.dueDay,
        })
      : debt.dueAt
        ? t('debts.planSingleSummary')
        : t('debts.planNoAgreement');

  return [person, debt.reference, plan].filter(Boolean).join(' · ');
}

function useDueText(debt: Debt): string {
  const { t } = useTranslation();

  if (!debt.nextDueAt) return t('debts.noDue');

  const days = daysUntil(debt.nextDueAt);
  return days > RELATIVE_DAYS ? formatDate(debt.nextDueAt) : formatRelativeDays(days);
}

function Row({
  debt,
  organization,
  showPerson,
}: {
  debt: Debt;
  organization: Organization;
  showPerson: boolean;
}) {
  const { t } = useTranslation();
  const [paying, setPaying] = useState(false);
  const bucket = bucketOf(debt);
  const meta = useMetaLine(debt, showPerson ? personOf(debt) : undefined);
  const due = useDueText(debt);
  const percent = percentPaid(debt);

  return (
    <div className="group relative flex items-center gap-2 rounded-lg py-3 pl-4 transition-colors hover:bg-muted/40">
      <span
        aria-hidden
        className={`absolute inset-y-2.5 left-0 w-0.75 rounded-full ${SPINE[bucket]}`}
      />

      <Link
        to="/organizations/$organizationId/debts/$debtId"
        params={{ organizationId: organization.id, debtId: debt.id }}
        className="flex min-w-0 flex-1 items-center gap-4 rounded-md text-left outline-offset-4"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{debt.creditor}</p>
          {meta && <p className="truncate text-xs text-muted-foreground">{meta}</p>}
          {percent > 0 && (
            <ProgressRule
              percent={percent}
              total={debt.totalCents}
              className="mt-2 h-0.75 max-w-40"
            />
          )}
        </div>

        <div className="shrink-0 text-right">
          <p className="text-base font-medium tabular-nums">
            {formatEuro(debt.remainingCents)}
          </p>
          <p className={`text-xs tabular-nums ${DUE_TONE[bucket]}`}>{due}</p>
        </div>
      </Link>

      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={t('debts.payNow', { creditor: debt.creditor })}
        onClick={() => setPaying(true)}
      >
        <Check />
      </Button>

      {paying && (
        <PaymentDialog
          organization={organization}
          debt={debt}
          suggestedCents={debt.nextDueCents ?? debt.remainingCents}
          onClose={() => setPaying(false)}
        />
      )}
    </div>
  );
}

function Settled({
  debts,
  organization,
  showPerson,
}: {
  debts: Debt[];
  organization: Organization;
  showPerson: boolean;
}) {
  const { t } = useTranslation();

  return (
    <details className="group flex flex-col">
      <summary className="flex cursor-pointer list-none items-center gap-3 outline-offset-4 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-3 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
        <h2 className={`${EYEBROW} text-muted-foreground`}>{t('debts.settledTitle')}</h2>
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground tabular-nums">{debts.length}</span>
      </summary>

      <div className="mt-1 flex flex-col">
        {debts.map((debt) => (
          <Link
            key={debt.id}
            to="/organizations/$organizationId/debts/$debtId"
            params={{ organizationId: organization.id, debtId: debt.id }}
            className="flex items-baseline gap-4 rounded-lg py-2 pl-6 text-sm text-muted-foreground outline-offset-4 transition-colors hover:bg-muted/40"
          >
            <span className="min-w-0 flex-1 truncate">
              {[showPerson ? personOf(debt) : undefined, debt.creditor]
                .filter(Boolean)
                .join(' · ')}
            </span>
            {debt.settledAt && (
              <span className="shrink-0 text-xs tabular-nums">
                {formatDate(debt.settledAt)}
              </span>
            )}
            <span className="shrink-0 tabular-nums">{formatEuro(debt.totalCents)}</span>
          </Link>
        ))}
      </div>
    </details>
  );
}

function Empty({ organization }: { organization: Organization }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-start gap-4 rounded-xl border border-dashed px-6 py-8">
      <p className="text-sm text-muted-foreground">{t('debts.noneHint')}</p>
      <Button
        render={
          <Link
            to="/organizations/$organizationId/debts/new"
            params={{ organizationId: organization.id }}
          />
        }
      >
        <Plus />
        {t('debts.add')}
      </Button>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3 border-b pb-6">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-1 w-full" />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    </div>
  );
}

export function DebtDialog({
  organization,
  debt,
  onClose,
  onCreated,
}: {
  organization: Organization;
  debt?: Debt;
  onClose: () => void;
  onCreated?: (debtId: string) => void;
}) {
  const { t } = useTranslation();
  const { user } = useSession();
  const isAdmin = organization.role === 'owner';
  const members = useStaffMembers(isAdmin ? organization.id : null);
  const [debtor, setDebtor] = useState(debt?.debtorId ?? user?.id ?? '');
  const [creditor, setCreditor] = useState(debt?.creditor ?? '');
  const [reference, setReference] = useState(debt?.reference ?? '');
  const [iban, setIban] = useState(debt?.iban ?? '');
  const [note, setNote] = useState(debt?.note ?? '');
  const [total, setTotal] = useState(toAmountInput(debt?.totalCents));
  const [plan, setPlan] = useState<DebtPlan>(debt?.plan ?? 'single');
  const [dueAt, setDueAt] = useState(toDateInput(debt?.dueAt));
  const [installment, setInstallment] = useState(toAmountInput(debt?.installmentCents));
  const [dueDay, setDueDay] = useState(debt?.dueDay ? String(debt.dueDay) : '1');

  const create = useCreateDebt();
  const update = useUpdateDebt();
  const mutation = debt ? update : create;

  const planLabels: Record<DebtPlan, string> = {
    single: t('debts.planSingle'),
    installment: t('debts.planInstallment'),
  };

  const personLabels: Record<string, string> = Object.fromEntries(
    (members.data ?? []).map((member) => [member.userId, member.name ?? member.email]),
  );

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    const shared = {
      debtorId: isAdmin && debtor ? debtor : undefined,
      creditor,
      totalCents: toCents(total),
      plan,
      dueAt: plan === 'single' ? fromDateInput(dueAt) : null,
      installmentCents: plan === 'installment' ? toCents(installment) : null,
      dueDay: plan === 'installment' ? Number(dueDay) : null,
    };

    if (debt) {
      update.mutate(
        {
          organizationId: organization.id,
          debtId: debt.id,
          ...shared,
          reference: reference.trim() || null,
          iban: iban.trim() || null,
          note: note.trim() || null,
        },
        { onSuccess: onClose },
      );
      return;
    }

    create.mutate(
      {
        organizationId: organization.id,
        ...shared,
        dueAt: shared.dueAt ?? undefined,
        installmentCents: shared.installmentCents ?? undefined,
        dueDay: shared.dueDay ?? undefined,
        reference: reference.trim() || undefined,
        iban: iban.trim() || undefined,
        note: note.trim() || undefined,
      },
      {
        onSuccess: (data) =>
          onCreated ? onCreated(data.debt.id) : onClose(),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{debt ? t('debts.editTitle') : t('debts.add')}</DialogTitle>
          <DialogDescription>{t('debts.dialogHint')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <FieldGroup>
            {isAdmin && (
              <Field>
                <FieldLabel htmlFor="debt-person">{t('debts.personLabel')}</FieldLabel>
                <Select
                  items={personLabels}
                  value={debtor}
                  onValueChange={(value) => setDebtor(String(value ?? ''))}
                >
                  <SelectTrigger id="debt-person" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {members.data?.map((member) => (
                      <SelectItem key={member.userId} value={member.userId}>
                        {member.name ?? member.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldDescription>{t('debts.personHint')}</FieldDescription>
              </Field>
            )}

            <Field>
              <FieldLabel htmlFor="debt-creditor">{t('debts.creditorLabel')}</FieldLabel>
              <Input
                id="debt-creditor"
                required
                autoFocus
                maxLength={120}
                value={creditor}
                onChange={(e) => setCreditor(e.target.value)}
                placeholder={t('debts.creditorPlaceholder')}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="debt-reference">{t('debts.referenceLabel')}</FieldLabel>
              <Input
                id="debt-reference"
                maxLength={120}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('debts.referencePlaceholder')}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="debt-iban">{t('debts.ibanLabel')}</FieldLabel>
              <Input
                id="debt-iban"
                maxLength={42}
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                placeholder={t('debts.ibanPlaceholder')}
              />
              <FieldDescription>{t('debts.ibanHint')}</FieldDescription>
            </Field>

            <Field>
              <FieldLabel htmlFor="debt-total">{t('debts.totalLabel')}</FieldLabel>
              <Input
                id="debt-total"
                type="number"
                required
                min="0.01"
                step="0.01"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                placeholder="0,00"
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="debt-plan">{t('debts.planLabel')}</FieldLabel>
              <Select
                items={planLabels}
                value={plan}
                onValueChange={(value) => setPlan(value as DebtPlan)}
              >
                <SelectTrigger id="debt-plan" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">{planLabels.single}</SelectItem>
                  <SelectItem value="installment">{planLabels.installment}</SelectItem>
                </SelectContent>
              </Select>
              <FieldDescription>{t('debts.planHint')}</FieldDescription>
            </Field>

            {plan === 'single' ? (
              <Field>
                <FieldLabel htmlFor="debt-due">{t('debts.dueLabel')}</FieldLabel>
                <Input
                  id="debt-due"
                  type="date"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
                <FieldDescription>{t('debts.dueHint')}</FieldDescription>
              </Field>
            ) : (
              <>
                <Field>
                  <FieldLabel htmlFor="debt-installment">
                    {t('debts.installmentLabel')}
                  </FieldLabel>
                  <Input
                    id="debt-installment"
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    value={installment}
                    onChange={(e) => setInstallment(e.target.value)}
                    placeholder="0,00"
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="debt-due-day">{t('debts.dueDayLabel')}</FieldLabel>
                  <Input
                    id="debt-due-day"
                    type="number"
                    required
                    min="1"
                    max="31"
                    step="1"
                    value={dueDay}
                    onChange={(e) => setDueDay(e.target.value)}
                  />
                  <FieldDescription>{t('debts.dueDayHint')}</FieldDescription>
                </Field>
              </>
            )}

            <Field>
              <FieldLabel htmlFor="debt-note">{t('debts.noteLabel')}</FieldLabel>
              <Input
                id="debt-note"
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('debts.notePlaceholder')}
              />
            </Field>

            {!debt && <FieldDescription>{t('debts.paymentsAfterCreate')}</FieldDescription>}

            <ErrorNote error={mutation.error} />
          </FieldGroup>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : debt ? (
                <Check />
              ) : (
                <Plus />
              )}
              {debt ? t('debts.save') : t('debts.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
